import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowUpRight, Award, Banknote, ChevronDown, GraduationCap, Hammer,
  HeartHandshake, MapPin, Medal, Paintbrush, Phone, Truck,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Kicker, Reveal, useTitle } from "../components/Reveal";
import { CONTACT } from "../data/content";

const JOBS = [
  {
    id: "geselle",
    icon: Paintbrush,
    title: "Maler & Lackierer – Geselle (m/w/d)",
    badges: ["Vollzeit", "Ab sofort"],
    intro: "Du beherrschst dein Handwerk, arbeitest sauber und willst Ergebnisse, auf die du stolz bist? Dann bist du bei uns richtig.",
    aufgaben: [
      "Innen- und Außenanstriche, Lackier- und Spachtelarbeiten",
      "Fassadenanstriche und -sanierungen",
      "Tapezier- und Untergrundarbeiten in Alt- und Neubau",
      "Selbstständige Ausführung beim Kunden vor Ort",
    ],
    profil: [
      "Abgeschlossene Ausbildung als Maler & Lackierer (m/w/d)",
      "Sauberes, eigenverantwortliches Arbeiten",
      "Freundliches Auftreten beim Kunden",
      "Führerschein Klasse B von Vorteil",
    ],
  },
  {
    id: "meister",
    icon: Medal,
    title: "Malermeister / Vorarbeiter (m/w/d)",
    badges: ["Vollzeit", "Ab sofort"],
    intro: "Du willst Verantwortung übernehmen, Baustellen führen und dein Wissen weitergeben? Bei uns bist du Meister – nicht Nummer.",
    aufgaben: [
      "Eigenverantwortliche Leitung von Baustellen",
      "Anleitung von Gesellen, Helfern und Auszubildenden",
      "Qualitätskontrolle, Aufmaß und Materialdisposition",
      "Ansprechpartner für Kunden vor Ort",
    ],
    profil: [
      "Meisterbrief im Maler- und Lackiererhandwerk – oder erfahrener Geselle mit Ambition zur Meisterschule",
      "Mehrjährige Berufserfahrung",
      "Organisationstalent und Verlässlichkeit",
      "Führerschein Klasse B",
    ],
  },
  {
    id: "ausbildung",
    icon: GraduationCap,
    title: "Ausbildung Maler & Lackierer (m/w/d)",
    badges: ["Ausbildung · 3 Jahre", "Start August 2027"],
    intro: "Lerne ein Handwerk mit Zukunft – in einem Betrieb, der seit 1925 ausbildet. Schnupperpraktikum jederzeit möglich.",
    aufgaben: [
      "Alle Techniken des Maler- und Lackiererhandwerks von Grund auf",
      "Mitarbeit auf echten Baustellen ab dem ersten Tag",
      "Berufsschule und überbetriebliche Lehrgänge",
      "Feste Ansprechpartner, die dir alles zeigen",
    ],
    profil: [
      "Schulabschluss (Haupt- oder Realschule)",
      "Handwerkliches Interesse und Farbgefühl",
      "Zuverlässigkeit und Lust, etwas zu lernen",
      "Vorpraktikum gern gesehen – aber kein Muss",
    ],
  },
  {
    id: "helfer",
    icon: Hammer,
    title: "Helfer / Quereinsteiger (m/w/d)",
    badges: ["Vollzeit oder Teilzeit", "Ab sofort"],
    intro: "Du packst gern mit an und willst in einem eingespielten Team arbeiten? Vorkenntnisse sind gut – Einsatz ist besser.",
    aufgaben: [
      "Vor- und Nacharbeiten auf der Baustelle (Abkleben, Abdecken, Aufräumen)",
      "Unterstützung der Gesellen bei Anstrich- und Spachtelarbeiten",
      "Materialtransport und Baustellenlogistik",
    ],
    profil: [
      "Körperliche Fitness und Zuverlässigkeit",
      "Sorgfalt und Teamgeist",
      "Erfahrung auf dem Bau von Vorteil – kein Muss",
    ],
  },
];

const POSITIONS = [...JOBS.map((j) => j.title), "Initiativbewerbung"];

const BENEFITS = [
  { icon: Award, title: "Meisterbetrieb seit 1925", text: "Ein Name, der in Olpe und Umgebung seit drei Generationen für Qualität steht – und für sichere Arbeitsplätze." },
  { icon: Banknote, title: "Faire, pünktliche Bezahlung", text: "Leistungsgerechter Lohn, pünktlich auf dem Konto. Zuschläge und Sonderzahlungen nach Absprache." },
  { icon: MapPin, title: "Baustellen in der Region", text: "Kurze Wege statt Montage: Unsere Projekte liegen in Olpe und dem Südsauerland. Abends bist du zu Hause." },
  { icon: Truck, title: "Moderne Ausstattung", text: "Gepflegter Fuhrpark, hochwertiges Werkzeug und Markenmaterial – damit du sauber arbeiten kannst." },
  { icon: GraduationCap, title: "Weiterbildung & Aufstieg", text: "Vom Helfer zum Gesellen, vom Gesellen zur Meisterschule: Wer will, den fördern wir." },
  { icon: HeartHandshake, title: "Familiäres Miteinander", text: "Feste Ansprechpartner, ehrliches Wort, Rückhalt im Team – hier kennt jeder jeden." },
];

const STEPS = [
  { nr: "01", title: "Kurzbewerbung senden", text: "Formular ausfüllen oder anrufen – für den Anfang genügen uns Name und ein paar Sätze zu dir. Lebenslauf kannst du später nachreichen." },
  { nr: "02", title: "Persönliches Kennenlernen", text: "Wir melden uns innerhalb weniger Tage und laden dich zu einem Gespräch in der Werkstatt ein – unkompliziert, auf Augenhöhe." },
  { nr: "03", title: "Probearbeiten & Start", text: "Ein Schnuppertag auf der Baustelle, damit beide Seiten wissen, ob es passt. Danach steht deinem Start nichts im Weg." },
];

const JOBS_LD = {
  "@context": "https://schema.org",
  "@graph": JOBS.map((j) => ({
    "@type": "JobPosting",
    title: j.title,
    description: j.intro,
    datePosted: "2026-09-14",
    employmentType: j.id === "ausbildung" ? "APPRENTICESHIP" : "FULL_TIME",
    hiringOrganization: {
      "@type": "Organization",
      name: "Manfred Berglar, Malermeister",
      sameAs: "https://maler-berglar.de",
    },
    jobLocation: {
      "@type": "Place",
      address: { "@type": "PostalAddress", addressLocality: "Olpe-Griesemert", addressRegion: "NRW", addressCountry: "DE" },
    },
  })),
};

const initialForm = { name: "", telefon: "", email: "", position: "", nachricht: "", datenschutz: false };

const JobCard = ({ job, onApply }) => {
  const [open, setOpen] = useState(false);
  const Icon = job.icon;
  return (
    <Reveal>
      <article data-testid={`job-card-${job.id}`} className="border border-line bg-white rounded-2xl overflow-hidden transition-shadow duration-300 hover:shadow-[0_10px_40px_-18px_rgba(30,39,28,0.35)]">
        <button
          onClick={() => setOpen((o) => !o)}
          data-testid={`job-toggle-${job.id}`}
          aria-expanded={open}
          className="w-full text-left p-6 md:p-8 flex items-start gap-5"
        >
          <span className="w-12 h-12 rounded-xl bg-surface border border-line flex items-center justify-center shrink-0" aria-hidden="true">
            <Icon size={22} className="text-golddark" strokeWidth={1.6} />
          </span>
          <span className="flex-1 min-w-0">
            <span className="flex flex-wrap items-center gap-2 mb-2">
              {job.badges.map((b) => (
                <span key={b} className="font-mono text-[10px] uppercase tracking-[0.18em] font-bold text-golddark bg-surface border border-line px-2.5 py-1 rounded-full">
                  {b}
                </span>
              ))}
            </span>
            <span className="block font-display text-xl md:text-2xl font-semibold text-ink tracking-tight">{job.title}</span>
            <span className="block text-muted text-sm leading-relaxed mt-2">{job.intro}</span>
          </span>
          <ChevronDown size={20} className={`text-golddark shrink-0 mt-1 transition-transform duration-300 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
        {open && (
          <div data-testid={`job-details-${job.id}`} className="px-6 md:px-8 pb-8 pt-2 border-t border-line/70">
            <div className="grid sm:grid-cols-2 gap-8 pt-6">
              <div>
                <p className="kicker !text-[11px] mb-3">Deine Aufgaben</p>
                <ul className="space-y-2.5">
                  {job.aufgaben.map((a) => (
                    <li key={a} className="flex gap-3 text-sm text-ink/80 leading-relaxed">
                      <span className="w-1.5 h-1.5 bg-gold rotate-45 mt-1.5 shrink-0" aria-hidden="true" />
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="kicker !text-[11px] mb-3">Das bringst du mit</p>
                <ul className="space-y-2.5">
                  {job.profil.map((p) => (
                    <li key={p} className="flex gap-3 text-sm text-ink/80 leading-relaxed">
                      <span className="w-1.5 h-1.5 bg-gold rotate-45 mt-1.5 shrink-0" aria-hidden="true" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <button onClick={() => onApply(job.title)} data-testid={`job-apply-${job.id}`} className="btn-primary mt-8">
              Jetzt bewerben <ArrowUpRight size={16} />
            </button>
          </div>
        )}
      </article>
    </Reveal>
  );
};

export default function Karriere() {
  useTitle("Karriere & Jobs – Manfred Berglar · Malermeister seit 1925");
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.text = JSON.stringify(JOBS_LD);
    document.head.appendChild(s);
    return () => document.head.removeChild(s);
  }, []);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const applyFor = (title) => {
    setForm((f) => ({ ...f, position: title }));
    const y = formRef.current?.getBoundingClientRect().top + window.scrollY - 90;
    window.__lenis ? window.__lenis.scrollTo(y) : window.scrollTo({ top: y, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending || !form.datenschutz) return;
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bewerbung`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          telefon: form.telefon,
          email: form.email,
          position: form.position,
          nachricht: form.nachricht,
        }),
      });
      if (!res.ok) throw new Error("send failed");
      toast.success("Deine Bewerbung ist eingegangen.", {
        description: "Wir melden uns innerhalb weniger Tage persönlich bei dir. Danke für dein Interesse!",
      });
      setForm(initialForm);
    } catch {
      toast.error("Senden leider fehlgeschlagen.", {
        description: `Bitte versuche es erneut – oder ruf einfach an: ${CONTACT.phoneDisplay}.`,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white text-ink overflow-x-clip">
      <div className="pt-[76px] md:pt-[88px]">

        {/* Hero */}
        <section className="bg-navy bg-grain text-white relative overflow-hidden" data-testid="karriere-hero">
          <img
            src="/images/marke/renovierung-logo.jpg"
            alt=""
            aria-hidden="true"
            className="absolute right-0 top-0 h-full w-[46%] object-cover opacity-20 pointer-events-none select-none hidden lg:block"
          />
          <div className="absolute inset-y-0 right-0 w-[46%] bg-gradient-to-r from-navy via-navy/70 to-transparent pointer-events-none hidden lg:block" aria-hidden="true" />
          <div className="container-x py-24 md:py-32 relative">
            <Reveal>
              <p className="kicker-dark">Karriere bei Berglar</p>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] mt-5 max-w-3xl">
                Dein Handwerk. Unser Name. <span className="font-fraktur text-herald">Seit 1925.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="text-white/75 leading-relaxed max-w-xl mt-6">
                Wir suchen Menschen, die ihr Handwerk ernst nehmen – vom Auszubildenden bis zum Meister.
                Ehrliche Arbeit, faire Bezahlung, Baustellen in der Region. Klingt gut? Dann lies weiter.
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <div className="flex flex-wrap gap-4 mt-10">
                <button onClick={() => applyFor("")} data-testid="hero-apply-button" className="btn-primary">
                  Jetzt bewerben <ArrowUpRight size={16} />
                </button>
                <a href={CONTACT.phoneHref} data-testid="hero-phone-link" className="inline-flex items-center gap-2.5 text-white/85 hover:text-herald transition-colors duration-300 font-mono text-sm tracking-wide px-2 py-3">
                  <Phone size={15} /> {CONTACT.phoneDisplay}
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="flex flex-wrap gap-x-12 gap-y-6 mt-14 pt-10 border-t border-white/15">
                {[["1925", "gegründet"], ["3", "Generationen"], [`${JOBS.length}`, "offene Stellen"]].map(([n, l]) => (
                  <div key={l}>
                    <p className="font-display text-3xl md:text-4xl font-semibold text-herald">{n}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/60 mt-1">{l}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-24 md:py-32 border-b border-line" data-testid="karriere-benefits">
          <div className="container-x">
            <Reveal>
              <Kicker>Warum Berglar?</Kicker>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.08] mt-4 max-w-2xl">
                Gute Gründe für einen <span className="font-fraktur text-golddark">Wechsel.</span>
              </h2>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-14">
              {BENEFITS.map((b, i) => {
                const Icon = b.icon;
                return (
                  <Reveal key={b.title} delay={0.06 * i}>
                    <div data-testid={`benefit-${i}`} className="border border-line bg-surface rounded-2xl p-7 h-full">
                      <Icon size={26} className="text-golddark" strokeWidth={1.5} />
                      <p className="font-display text-lg font-semibold text-ink mt-5">{b.title}</p>
                      <p className="text-muted text-sm leading-relaxed mt-2">{b.text}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* Stellen */}
        <section className="py-24 md:py-32 bg-surface border-b border-line" data-testid="karriere-jobs">
          <div className="container-x">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12 md:mb-16">
              <div>
                <Reveal>
                  <Kicker>Offene Stellen</Kicker>
                </Reveal>
                <Reveal delay={0.08}>
                  <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.08] mt-4">
                    Wen wir <span className="font-fraktur text-golddark">suchen.</span>
                  </h2>
                </Reveal>
              </div>
              <Reveal delay={0.16} className="max-w-md">
                <p className="text-muted leading-relaxed">
                  Keine passende Stelle dabei? Bewirb dich initiativ – gute Leute finden bei uns immer einen Platz.
                </p>
              </Reveal>
            </div>
            <div className="flex flex-col gap-5">
              {JOBS.map((job) => (
                <JobCard key={job.id} job={job} onApply={applyFor} />
              ))}
            </div>
          </div>
        </section>

        {/* Prozess */}
        <section className="py-24 md:py-32 border-b border-line" data-testid="karriere-prozess">
          <div className="container-x">
            <Reveal>
              <Kicker>So läuft's ab</Kicker>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.08] mt-4 max-w-2xl">
                In drei Schritten <span className="font-fraktur text-golddark">zu uns.</span>
              </h2>
            </Reveal>
            <div className="grid md:grid-cols-3 gap-5 mt-14">
              {STEPS.map((s, i) => (
                <Reveal key={s.nr} delay={0.08 * i}>
                  <div data-testid={`prozess-step-${i + 1}`} className="border border-line rounded-2xl p-7 h-full relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-[3px] bg-gold" aria-hidden="true" />
                    <p className="font-fraktur text-4xl text-golddark/40">{s.nr}</p>
                    <p className="font-display text-lg font-semibold text-ink mt-4">{s.title}</p>
                    <p className="text-muted text-sm leading-relaxed mt-2">{s.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Bewerbungsformular */}
        <section ref={formRef} id="bewerbung" className="py-24 md:py-32 bg-surface" data-testid="karriere-bewerbung">
          <div className="container-x">
            <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
              <div className="lg:col-span-5">
                <Reveal>
                  <Kicker>Kurzbewerbung</Kicker>
                </Reveal>
                <Reveal delay={0.08}>
                  <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.08] mt-4">
                    Zwei Minuten – <span className="font-fraktur text-golddark">mehr braucht es nicht.</span>
                  </h2>
                </Reveal>
                <Reveal delay={0.16}>
                  <p className="text-muted leading-relaxed mt-6 max-w-md">
                    Kein Anschreiben, keine Mappe: Erzähl uns kurz, wer du bist und was du kannst.
                    Lebenslauf und Zeugnisse kannst du später nachreichen. Oder ruf einfach an:
                  </p>
                </Reveal>
                <Reveal delay={0.22}>
                  <a href={CONTACT.phoneHref} data-testid="bewerbung-phone-link" className="inline-flex items-center gap-3 mt-6 font-display text-2xl font-semibold text-ink hover:text-golddark transition-colors duration-300">
                    <Phone size={22} className="text-golddark" /> {CONTACT.phoneDisplay}
                  </a>
                </Reveal>
              </div>
              <Reveal delay={0.1} className="lg:col-span-7">
                <form onSubmit={handleSubmit} data-testid="bewerbung-form" className="border border-line border-t-[3px] border-t-gold bg-white rounded-2xl p-8 md:p-12 flex flex-col gap-7">
                  <div className="grid sm:grid-cols-2 gap-7">
                    <div>
                      <label htmlFor="bw-name" className="kicker !text-[11px] block mb-1">Name *</label>
                      <input id="bw-name" data-testid="bewerbung-input-name" required value={form.name} onChange={set("name")} className="input-line" placeholder="Dein Name" />
                    </div>
                    <div>
                      <label htmlFor="bw-telefon" className="kicker !text-[11px] block mb-1">Telefonnummer</label>
                      <input id="bw-telefon" data-testid="bewerbung-input-phone" type="tel" value={form.telefon} onChange={set("telefon")} className="input-line" placeholder="Für Rückfragen" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-7">
                    <div>
                      <label htmlFor="bw-email" className="kicker !text-[11px] block mb-1">E-Mail *</label>
                      <input id="bw-email" data-testid="bewerbung-input-email" type="email" required value={form.email} onChange={set("email")} className="input-line" placeholder="deine@mail.de" />
                    </div>
                    <div>
                      <label htmlFor="bw-position" className="kicker !text-[11px] block mb-1">Wunschposition</label>
                      <Select value={form.position} onValueChange={(v) => setForm((f) => ({ ...f, position: v }))}>
                        <SelectTrigger
                          id="bw-position"
                          data-testid="bewerbung-select-position"
                          className="w-full h-auto rounded-none border-0 border-b-2 border-line bg-transparent px-0 py-3 text-base text-ink shadow-none focus:ring-0 focus:border-gold data-[placeholder]:text-muted/60"
                        >
                          <SelectValue placeholder="Bitte wählen" />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl border-line bg-white text-ink">
                          {POSITIONS.map((p) => (
                            <SelectItem key={p} value={p} className="rounded-lg focus:bg-surface focus:text-ink" data-testid={`bewerbung-option-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                              {p}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="bw-nachricht" className="kicker !text-[11px] block mb-1">Über dich *</label>
                    <textarea id="bw-nachricht" data-testid="bewerbung-input-message" required rows={5} value={form.nachricht} onChange={set("nachricht")} className="input-line resize-none" placeholder="Wer bist du, was hast du bisher gemacht, ab wann kannst du anfangen? Ein paar Sätze genügen." />
                  </div>
                  <label className="flex items-start gap-3 cursor-pointer py-2 -my-1" data-testid="bewerbung-privacy-label">
                    <input
                      type="checkbox"
                      required
                      checked={form.datenschutz}
                      onChange={set("datenschutz")}
                      data-testid="bewerbung-privacy-checkbox"
                      className="mt-1 h-4 w-4 shrink-0 accent-[#8A6D43] cursor-pointer"
                    />
                    <span className="text-sm text-muted leading-relaxed">
                      Ich habe die{" "}
                      <a href="/datenschutz" target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-golddark transition-colors">
                        Datenschutzerklärung
                      </a>{" "}
                      gelesen und bin mit der Verarbeitung meiner Angaben zur Bearbeitung der Bewerbung einverstanden. *
                    </span>
                  </label>
                  <button type="submit" disabled={sending} data-testid="bewerbung-submit-button" className="btn-primary self-start disabled:opacity-60">
                    {sending ? "Wird gesendet …" : "Bewerbung absenden"} <ArrowUpRight size={16} />
                  </button>
                  <p className="text-xs text-muted leading-relaxed">
                    Deine Daten verwenden wir ausschließlich zur Bearbeitung deiner Bewerbung und löschen sie
                    spätestens sechs Monate nach Abschluss des Verfahrens.
                  </p>
                </form>
              </Reveal>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
